import React, {ReactNode} from "react";
import {Stack} from "@mui/material";
import {AxiosError} from "axios";
import {BackHomeButton} from "./BackHomeButton";
import {ErrorResponse} from "react-router-dom";
import {SignInButton} from "./SignInButton";
import {DefaultOfficeButton} from "./DefaultOfficeButton";

export class NavigationError extends Error {
    constructor(
        readonly status: string,
        readonly statusText: string,
        readonly message: string,
        readonly action: ReactNode = <BackHomeButton/>,
    ) {
        super(message);
    }

    static unauthorized(message?: string) {
        return new NavigationError(
            'Sign in',
            'Who are you?',
            message || 'Please sign in to access the page.',
            <SignInButton/>,
        );
    }

    static forbidden(message?: string) {
        return new NavigationError(
            '403',
            'Forbidden',
            message || 'To access this page, you must have the necessary permissions. Please contact an administrator if you believe you should have access to this page.',
            <Stack direction="row" gap={1}>
                <BackHomeButton/>
                <SignInButton/>
            </Stack>
        );
    }

    static jetBrainsOnly(message?: string) {
        return new NavigationError(
            '403',
            'Forbidden',
            message || 'To access this page, you must be a JetBrains employee. Please contact an administrator if you believe you should have access to this page.',
            <Stack direction="row" gap={1}>
                <BackHomeButton/>
                <SignInButton/>
            </Stack>
        );
    }

    static forbiddenOffice(message?: string) {
        return new NavigationError(
            '403',
            'Forbidden',
            message || 'To access this page, you must have permissions to access this office. Please contact an administrator if you believe you should have access to this page.',
            <Stack direction="row" gap={1}>
                <DefaultOfficeButton/>
                <SignInButton/>
            </Stack>
        );
    }

    static notFound(message?: string) {
        return new NavigationError(
            '404',
            'Page Not Found',
            message || 'The page you are looking for doesn\'t exist or has been moved.',
        );
    }

    static fromAxiosError(error: AxiosError) {
        const message = (error as any as AxiosError<{error: string}>).response?.data?.error;
        if (error.isAxiosError && error.status === 401) {
            return NavigationError.unauthorized(message);
        } else if (error.isAxiosError && error.status === 403) {
            return NavigationError.forbidden(message);
        } else if (error.isAxiosError && error.status === 404) {
            return NavigationError.notFound(message);
        }
        return new NavigationError(
            error.status?.toString() || error.name,
            error.response?.statusText || error.message,
            message || error.message,
        );
    }

    static fromErrorResponse(error: ErrorResponse) {
        let message;
        if (typeof error.data === 'string') {
            message = error.data;
        } else if (error.data && typeof (error.data as any).message === 'string') {
            message = (error.data as any).message;
        } else {
            message = 'Something went wrong';
        }
        if (error.status === 401) {
            return NavigationError.unauthorized(message);
        } else if (error.status === 403) {
            return NavigationError.forbidden(message);
        } else if (error.status === 404) {
            return NavigationError.notFound(message);
        }
        return new NavigationError(
            error.status.toString(),
            error.statusText,
            message,
        );
    }
}
